import * as React from "react";
import { Calendar } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { useDateField } from "../DatePicker.js";
import { Input } from "./input.js";

// A date field that looks like every other shadcn Input: same height, border,
// radius and focus ring, with the calendar button sitting inside the field on
// the right. Behaviour (typed DD/MM/YYYY or MM/DD/YYYY entry, min/max checks,
// native calendar popup) is shared with the legacy DatePicker via useDateField,
// so the two can never drift apart — only their styling differs.

export interface DateInputProps {
  id?: string | undefined;
  value: string;
  onChange: (iso: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  disabled?: boolean | undefined;
  isUS?: boolean | undefined;
  className?: string | undefined;
}

function DateInput({ id, value, onChange, min, max, disabled, isUS, className }: DateInputProps) {
  const { display, error, hiddenRef, handleChange, handlePickerChange, openPicker, placeholder } =
    useDateField({ value, onChange, min, max, isUS });

  return (
    <div className={cn("relative w-full", className)}>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={display}
        disabled={disabled}
        aria-invalid={error || undefined}
        onChange={(e) => handleChange(e.target.value)}
        className={cn(
          "pr-9",
          error && "border-[var(--color-error)] focus:ring-[var(--color-error)]"
        )}
      />
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        tabIndex={-1}
        aria-label="Open calendar"
        className="absolute inset-y-0 right-0 flex w-9 cursor-pointer items-center justify-center rounded-r-md text-surface-400 transition-colors hover:text-surface-700 disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Calendar size={15} />
      </button>
      {/* The native picker supplies the calendar popup; anchored at the field's
          bottom-left so the popup opens underneath it. */}
      <input
        ref={hiddenRef}
        type="date"
        value={value}
        min={min}
        max={max}
        disabled={disabled}
        onChange={(e) => handlePickerChange(e.target.value)}
        className="pointer-events-none absolute bottom-0 left-0 h-0 w-0 opacity-0"
        aria-hidden="true"
        tabIndex={-1}
      />
    </div>
  );
}

export { DateInput };
